/* eslint-disable */
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Minus, Award, AlertTriangle, StickyNote, Trash2, Plus, Users, X, Clock, MessageSquare, CheckCircle, Zap, Activity } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Area, AreaChart } from "recharts";
import HistoryModal from "./HistoryModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================================================
// LIVE MARKET RATES — vape sold per second/minute/hour/day (real-time)
// ============================================================================
export const LiveMarketRates = () => {
  const [data, setData] = useState(null);
  const [monthsRange, setMonthsRange] = useState(1); // 1 | 3 | 6
  const [view, setView] = useState("hour"); // 'sec' | 'min' | 'hour' | 'month'
  const [pulseSec, setPulseSec] = useState(0);

  const fetchLive = async () => {
    try {
      const r = await axios.get(`${API}/ceo/market-intelligence/live-rates?months=${monthsRange}`);
      setData(r.data);
      const secs = r.data?.last_60_sec || [];
      const last = secs[secs.length - 1];
      if (last && last.vapes > 0) setPulseSec(Date.now());
    } catch (_) { /* ignore */ }
  };

  useEffect(() => {
    fetchLive();
    const t = setInterval(fetchLive, 3000);
    return () => clearInterval(t);
  }, [monthsRange]);

  const chartData = useMemo(() => {
    if (!data) return [];
    if (view === "sec") return data.last_60_sec.map(b => ({ label: b.second, vapes: b.vapes, revenue: b.revenue }));
    if (view === "min") return data.last_60_min.map(b => ({ label: b.minute, vapes: b.vapes, revenue: b.revenue }));
    if (view === "month") return (data.monthly || []).map(b => ({ label: b.label, vapes: b.vapes, revenue: b.revenue }));
    return data.hourly.map(b => ({ label: b.label, vapes: b.vapes, revenue: b.revenue }));
  }, [data, view]);

  const totalRecent = useMemo(() => {
    if (!chartData) return { vapes: 0, revenue: 0 };
    return {
      vapes: chartData.reduce((a, b) => a + (b.vapes || 0), 0),
      revenue: chartData.reduce((a, b) => a + (b.revenue || 0), 0),
    };
  }, [chartData]);

  if (!data) {
    return (
      <div className="bg-gradient-to-br from-fuchsia-950/40 to-cyan-950/40 border-2 border-fuchsia-500/40 rounded-xl p-4 mb-4" data-testid="live-rates-loading">
        <p className="text-fuchsia-300 text-sm">⚡ Chajman Mache Live...</p>
      </div>
    );
  }

  const isPulsing = Date.now() - pulseSec < 2000;

  return (
    <div className="bg-gradient-to-br from-fuchsia-950/40 via-purple-950/40 to-cyan-950/40 border-2 border-fuchsia-500/40 rounded-xl p-4 mb-4" data-testid="live-market-rates">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h4 className="text-fuchsia-300 font-black text-sm flex items-center gap-2">
          <Zap className={`w-5 h-5 ${isPulsing ? "text-fuchsia-300 animate-pulse" : "text-fuchsia-400"}`} />
          MACHE VAPE LIVE
          <span className="text-[10px] font-normal text-zinc-500 uppercase tracking-widest">refresh 3s • {data.window_label}</span>
        </h4>
        <div className="flex gap-1">
          {[[1,"Mwa aktif"],[3,"3 mwa"],[6,"6 mwa"]].map(([m, lbl]) => (
            <button
              key={m}
              onClick={() => setMonthsRange(m)}
              data-testid={`live-rates-month-${m}`}
              className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase ${monthsRange === m ? "bg-fuchsia-600 text-white" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"}`}
            >
              {lbl}
            </button>
          ))}
        </div>
      </div>

      {/* 4 rate tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <div className="bg-zinc-950/70 border border-fuchsia-500/30 rounded-xl p-3 text-center" data-testid="rate-per-second">
          <p className="text-fuchsia-400 text-[10px] uppercase font-black tracking-widest">Pa Segond</p>
          <p className="text-white font-black text-2xl mt-1">{data.rates.per_second.toFixed(4)}</p>
          <p className="text-zinc-500 text-[9px] uppercase">vape / sek</p>
        </div>
        <div className="bg-zinc-950/70 border border-purple-500/30 rounded-xl p-3 text-center" data-testid="rate-per-minute">
          <p className="text-purple-400 text-[10px] uppercase font-black tracking-widest">Pa Minit</p>
          <p className="text-white font-black text-2xl mt-1">{data.rates.per_minute.toFixed(3)}</p>
          <p className="text-zinc-500 text-[9px] uppercase">vape / min</p>
        </div>
        <div className="bg-zinc-950/70 border border-cyan-500/30 rounded-xl p-3 text-center" data-testid="rate-per-hour">
          <p className="text-cyan-400 text-[10px] uppercase font-black tracking-widest">Pa Èdtan</p>
          <p className="text-white font-black text-2xl mt-1">{data.rates.per_hour.toFixed(2)}</p>
          <p className="text-zinc-500 text-[9px] uppercase">vape / h</p>
          <p className="text-emerald-400 text-[9px] font-bold">${data.rates.revenue_per_hour.toFixed(2)}/h</p>
        </div>
        <div className="bg-zinc-950/70 border border-emerald-500/30 rounded-xl p-3 text-center" data-testid="rate-per-day">
          <p className="text-emerald-400 text-[10px] uppercase font-black tracking-widest">Pa Jou</p>
          <p className="text-white font-black text-2xl mt-1">{data.rates.per_day.toFixed(1)}</p>
          <p className="text-zinc-500 text-[9px] uppercase">vape / jou</p>
          <p className="text-emerald-400 text-[9px] font-bold">${data.rates.revenue_per_day.toFixed(2)}/j</p>
        </div>
      </div>

      {/* Totals + live indicator */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3 bg-zinc-950/50 border border-zinc-800 rounded-xl p-2.5">
        <div className="flex items-center gap-3">
          <span className={`w-2.5 h-2.5 rounded-full ${isPulsing ? "bg-fuchsia-400 animate-ping" : "bg-red-400 animate-pulse"}`} />
          <div>
            <p className="text-zinc-400 text-[10px] uppercase">{data.window_label} — total</p>
            <p className="text-white font-black text-lg">
              {data.totals.vapes} <span className="text-zinc-500 text-xs font-normal">vape</span>
              <span className="text-emerald-400 ml-2">${data.totals.revenue.toFixed(2)}</span>
            </p>
          </div>
        </div>
        <div className="flex gap-1">
          {[["sec","Segond (60)"],["min","Minit (60)"],["hour","Èdtan (24)"],["month","Mwa"]].map(([k, lbl]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`live-view-${k}`}
              className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase ${view === k ? "bg-cyan-600 text-white" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"}`}>
              {lbl}
            </button>
          ))}
        </div>
      </div>

      {/* Live area chart */}
      <div style={{ width: "100%", height: 200 }} data-testid="live-chart">
        <ResponsiveContainer>
          <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="liveGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#e879f9" stopOpacity={0.8} />
                <stop offset="100%" stopColor="#0891b2" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis dataKey="label" stroke="#9ca3af" tick={{ fontSize: 10 }} interval={view === "sec" ? 9 : view === "min" ? 9 : 3} />
            <YAxis stroke="#9ca3af" tick={{ fontSize: 10 }} allowDecimals={false} />
            <Tooltip
              contentStyle={{ background: "#0a0a0a", border: "1px solid #a855f7", borderRadius: 8, fontSize: 12 }}
              formatter={(v, n) => n === "revenue" ? [`$${Number(v).toFixed(2)}`, "Revni"] : [v, "Vape"]}
            />
            <Area type="monotone" dataKey="vapes" stroke="#e879f9" strokeWidth={2} fill="url(#liveGrad)" isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <p className="text-zinc-500 text-[10px] text-center mt-1">
        {view === "sec" ? "Dènye 60 segond" : view === "min" ? "Dènye 60 minit" : "Dènye 24 èdtan"} — <span className="text-fuchsia-400 font-bold">{totalRecent.vapes}</span> vape • <span className="text-emerald-400 font-bold">${totalRecent.revenue.toFixed(2)}</span>
      </p>
    </div>
  );
};

// ============================================================================
// MARKET INTELLIGENCE TAB
// ============================================================================
export const MarketIntelligenceTab = () => {
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(6);
  const [loading, setLoading] = useState(true);
  const [targetRevenue, setTargetRevenue] = useState(10000);
  const [objective, setObjective] = useState(null);
  const [trendCompare, setTrendCompare] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ceo/market-intelligence?months=${months}`);
      setData(r.data);
      // Alerts: bottom sellers + inactive users (from activity-report)
      try {
        const act = await axios.get(`${API}/ceo/activity-report`);
        const inactiveList = (act.data.users || []).filter(u => u.inactive);
        const combined = [
          ...inactiveList.slice(0, 10).map(u => ({
            level: "critical",
            title: `${u.name} — PA AKTIF`,
            detail: `Dènye login: ${u.days_since_last_login !== null ? `${u.days_since_last_login}j` : "janm"} • Dènye vant: ${u.days_since_last_sale !== null ? `${u.days_since_last_sale}j` : "janm"}`,
            user_id: u.id,
          })),
          ...(r.data.bottom_sellers || []).slice(0, 10).map(b => ({
            level: "warning",
            title: `${b.name} — Vant fèb`,
            detail: `${b.sold} vape mwa sa (< 5)`,
            user_id: b.id,
          })),
        ];
        setAlerts(combined);
      } catch(_) { /* ignore */ }
    } catch (e) {
      toast.error("Erè chajman");
    } finally {
      setLoading(false);
    }
  };

  const fetchObjective = async () => {
    try {
      const r = await axios.get(`${API}/ceo/market-intelligence/objective?target_revenue=${targetRevenue}`);
      setObjective(r.data);
    } catch (e) { /* ignore */ }
  };

  const fetchTrendCompare = async () => {
    try {
      const r = await axios.get(`${API}/ceo/market-intelligence/trend-comparison`);
      setTrendCompare(r.data);
    } catch (e) { /* ignore */ }
  };

  const handleTargetChange = async (v) => {
    setSaving(true);
    setTargetRevenue(v);
    // simulate save spinner while objective refetches
    try {
      await axios.get(`${API}/ceo/market-intelligence/objective?target_revenue=${v}`).then(r => setObjective(r.data));
    } catch(_) {}
    setSaving(false);
  };

  useEffect(() => {
    fetchData();
    fetchObjective();
    fetchTrendCompare();
    // Real-time refresh every 15s
    const t = setInterval(() => { fetchData(); fetchObjective(); fetchTrendCompare(); }, 15000);
    return () => clearInterval(t);
  }, [months]);

  if (loading || !data) return <div className="text-center py-10 text-zinc-500">Chajman...</div>;

  const trendIcon = data.trend === "up" ? <TrendingUp className="w-6 h-6 text-emerald-400" /> : data.trend === "down" ? <TrendingDown className="w-6 h-6 text-red-400" /> : <Minus className="w-6 h-6 text-zinc-400" />;

  return (
    <div className="glass-panel p-6" data-testid="market-intel-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-white font-bold flex items-center gap-2">
          🌐 Market Intelligence
        </h3>
        <div className="flex gap-2">
          {[3, 6, 12].map(m => (
            <button key={m} onClick={() => setMonths(m)} data-testid={`mi-months-${m}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold ${months === m ? "bg-emerald-600 text-white" : "bg-zinc-800 text-zinc-400"}`}>
              {m} mwa
            </button>
          ))}
        </div>
      </div>

      {/* LIVE MARKET RATES — pa segond/minit/èdtan/jou */}
      <LiveMarketRates />

      {/* Trend banner */}
      <div className={`rounded-xl p-4 mb-4 border-2 ${data.trend === "up" ? "bg-emerald-950/40 border-emerald-500/40" : data.trend === "down" ? "bg-red-950/40 border-red-500/40" : "bg-zinc-900/60 border-zinc-700"}`}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            {trendIcon}
            <div>
              <p className="text-white font-black text-lg">Mache Vape a: {data.trend === "up" ? "AP MONTE ↑" : data.trend === "down" ? "AP DESANN ↓" : "STAB →"}</p>
              <p className="text-zinc-400 text-xs">{data.this_month} — Vent jodi: <span className="text-emerald-400 font-bold">{data.total_sold_this_month} vape</span> / <span className="text-yellow-400 font-bold">${(data.total_revenue_this_month || 0).toFixed(0)}</span></p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-red-400 rounded-full animate-pulse" title="Live" />
            <span className="text-[10px] text-zinc-500 uppercase">Live • refresh 15s</span>
          </div>
        </div>
      </div>

      {/* MONTH-OVER-MONTH TREND COMPARISON */}
      {trendCompare && (
        <div className="bg-gradient-to-br from-blue-950/40 to-violet-950/40 border border-blue-500/40 rounded-xl p-4 mb-4" data-testid="mi-trend-compare">
          <h4 className="text-blue-300 font-bold text-sm mb-3 flex items-center gap-2">
            📊 Konparezon Mwa pa Mwa {trendCompare.direction === "up" ? "↗️" : trendCompare.direction === "down" ? "↘️" : "→"}
          </h4>
          <div className="grid grid-cols-3 gap-2 mb-2">
            <div className="bg-zinc-950/60 rounded-lg p-2 text-center">
              <p className="text-zinc-500 text-[10px] uppercase">2 mwa avan</p>
              <p className="text-zinc-400 text-xs">{trendCompare.months.prev_prev.month}</p>
              <p className="text-white font-bold">{trendCompare.months.prev_prev.sold}</p>
              <p className="text-yellow-400 text-[10px]">${trendCompare.months.prev_prev.revenue.toFixed(0)}</p>
            </div>
            <div className="bg-zinc-950/60 rounded-lg p-2 text-center">
              <p className="text-zinc-500 text-[10px] uppercase">Mwa pase</p>
              <p className="text-zinc-400 text-xs">{trendCompare.months.previous.month}</p>
              <p className="text-white font-bold">{trendCompare.months.previous.sold}</p>
              <p className="text-yellow-400 text-[10px]">${trendCompare.months.previous.revenue.toFixed(0)}</p>
            </div>
            <div className={`rounded-lg p-2 text-center border ${trendCompare.direction === "up" ? "bg-emerald-950/40 border-emerald-500/40" : trendCompare.direction === "down" ? "bg-red-950/40 border-red-500/40" : "bg-zinc-950/60 border-zinc-700"}`}>
              <p className="text-zinc-500 text-[10px] uppercase">Kounye a</p>
              <p className="text-zinc-400 text-xs">{trendCompare.months.current.month}</p>
              <p className="text-white font-bold">{trendCompare.months.current.sold}</p>
              <p className="text-yellow-400 text-[10px]">${trendCompare.months.current.revenue.toFixed(0)}</p>
            </div>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-zinc-400">vs mwa pase: <span className={trendCompare.delta_pct_vs_prev >= 0 ? "text-emerald-400 font-bold" : "text-red-400 font-bold"}>{trendCompare.delta_pct_vs_prev > 0 ? "+" : ""}{trendCompare.delta_pct_vs_prev}%</span></span>
            <span className="text-zinc-400">vs 2 mwa avan: <span className={trendCompare.delta_pct_vs_prev_prev >= 0 ? "text-emerald-400 font-bold" : "text-red-400 font-bold"}>{trendCompare.delta_pct_vs_prev_prev > 0 ? "+" : ""}{trendCompare.delta_pct_vs_prev_prev}%</span></span>
          </div>
        </div>
      )}

      {/* ALERTS PANEL */}
      {alerts.length > 0 && (
        <div className="bg-red-950/20 border-2 border-red-500/40 rounded-xl p-4 mb-4" data-testid="mi-alerts-panel">
          <h4 className="text-red-300 font-bold text-sm mb-3 flex items-center gap-2">
            🚨 Alèt ({alerts.length}) — Moun ki bezwen atansyon
          </h4>
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {alerts.map((a, i) => (
              <div key={i} className={`rounded-lg p-2 text-xs flex items-center justify-between ${a.level === "critical" ? "bg-red-950/50 border border-red-500/40" : "bg-orange-950/40 border border-orange-500/30"}`} data-testid={`mi-alert-${i}`}>
                <div>
                  <p className={`font-bold ${a.level === "critical" ? "text-red-300" : "text-orange-300"}`}>{a.title}</p>
                  <p className="text-zinc-400 text-[10px]">{a.detail}</p>
                </div>
                <span className={`text-[9px] font-black px-2 py-0.5 rounded-full ${a.level === "critical" ? "bg-red-500 text-white" : "bg-orange-500 text-white"}`}>{a.level === "critical" ? "KRITIK" : "ATANSYON"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-gradient-to-br from-amber-950/40 to-purple-950/40 border-2 border-amber-500/40 rounded-xl p-4 mb-4" data-testid="mi-objective">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h4 className="text-amber-300 font-bold text-sm">🎯 Objektif Revni Mwa a</h4>
          <div className="flex gap-2">
            {[5000, 10000, 20000, 50000].map((v) => (
              <button key={v} onClick={() => handleTargetChange(v)} data-testid={`obj-preset-${v}`}
                disabled={saving}
                className={`px-3 py-1 rounded-lg text-xs font-bold disabled:opacity-40 ${targetRevenue === v ? "bg-amber-600 text-white" : "bg-zinc-800 text-zinc-400"}`}>
                ${(v/1000).toFixed(0)}k
              </button>
            ))}
            <input type="number" value={targetRevenue} onChange={(e) => setTargetRevenue(parseFloat(e.target.value) || 0)}
              className="w-24 bg-zinc-900 border border-zinc-700 rounded-lg px-2 py-1 text-white text-xs" placeholder="$" />
            <button onClick={() => handleTargetChange(targetRevenue)} disabled={saving} className="bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-lg disabled:opacity-50 flex items-center gap-1" data-testid="obj-calculate">
              {saving ? <><span className="w-3 h-3 border-2 border-white/50 border-t-white rounded-full animate-spin"/> Chajman...</> : "Kalkile"}
            </button>
          </div>
        </div>

        {objective ? (
          <>
            <p className={`font-bold text-sm mb-3 ${objective.on_track ? "text-emerald-300" : "text-orange-300"}`}>{objective.message}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
              <div className="bg-zinc-950/60 rounded-lg p-2 text-center">
                <p className="text-zinc-500 text-[10px] uppercase">Vape rete</p>
                <p className="text-white font-bold text-lg">{objective.vapes_remaining_needed}</p>
              </div>
              <div className="bg-zinc-950/60 rounded-lg p-2 text-center">
                <p className="text-zinc-500 text-[10px] uppercase">Pa jou</p>
                <p className="text-emerald-400 font-bold text-lg">{objective.vapes_per_day_needed}</p>
              </div>
              <div className="bg-zinc-950/60 rounded-lg p-2 text-center">
                <p className="text-zinc-500 text-[10px] uppercase">Stock men</p>
                <p className="text-amber-400 font-bold text-lg">{objective.total_stock_in_hands}</p>
              </div>
              <div className="bg-zinc-950/60 rounded-lg p-2 text-center">
                <p className="text-zinc-500 text-[10px] uppercase">Jou rete</p>
                <p className="text-blue-400 font-bold text-lg">{objective.days_left_in_month}</p>
              </div>
            </div>

            {objective.recommendations.give_more_to.length > 0 && (
              <div className="mb-2">
                <p className="text-emerald-400 text-xs font-bold mb-1">💪 Bay PLIS stock ba:</p>
                <div className="flex gap-2 flex-wrap">
                  {objective.recommendations.give_more_to.map(s => (
                    <span key={s.id} className="bg-emerald-950/50 border border-emerald-500/40 text-emerald-300 text-[10px] px-2 py-1 rounded-full">
                      {s.name} ({s.velocity_week} vape/sm)
                    </span>
                  ))}
                </div>
              </div>
            )}

            {objective.recommendations.reduce_stock_for.length > 0 && (
              <div className="mb-2">
                <p className="text-red-400 text-xs font-bold mb-1">📉 Diminye stock pou:</p>
                <div className="flex gap-2 flex-wrap">
                  {objective.recommendations.reduce_stock_for.map(s => (
                    <span key={s.id} className="bg-red-950/40 border border-red-500/40 text-red-300 text-[10px] px-2 py-1 rounded-full">
                      {s.name} ({s.current_stock} sou men)
                    </span>
                  ))}
                </div>
              </div>
            )}

            {objective.recommendations.top_cities.length > 0 && (
              <div>
                <p className="text-blue-400 text-xs font-bold mb-1">🏙️ Vil ki plus achte:</p>
                <div className="flex gap-2 flex-wrap">
                  {objective.recommendations.top_cities.map(c => (
                    <span key={c.city} className="bg-blue-950/40 border border-blue-500/40 text-blue-300 text-[10px] px-2 py-1 rounded-full">
                      {c.city} — {c.sold} vape
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : <p className="text-zinc-500 text-xs">Chajman objektif...</p>}
      </div>

      {/* Monthly trend chart */}
      <div className="bg-zinc-900/60 rounded-xl p-4 mb-4">
        <p className="text-white font-bold text-xs uppercase mb-2">📈 Vent pa Mwa</p>
        <div style={{ width: "100%", height: 240 }} data-testid="mi-monthly-chart">
          <ResponsiveContainer>
            <LineChart data={data.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="month" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #3f3f46" }} />
              <Legend />
              <Line type="monotone" dataKey="sold" name="Vape vann" stroke="#10b981" strokeWidth={3} />
              <Line type="monotone" dataKey="revenue" name="Revni $" stroke="#facc15" strokeWidth={2} yAxisId={0} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top / bottom sellers */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-emerald-950/30 border border-emerald-700/40 rounded-xl p-4">
          <p className="text-emerald-400 font-bold text-sm mb-2 flex items-center gap-2"><Award className="w-4 h-4" /> Top 10 — Mwa sa</p>
          {data.top_sellers.length === 0 ? <p className="text-zinc-500 text-xs">Pa gen vant mwa sa poko.</p> : (
            <div className="space-y-1">
              {data.top_sellers.map((t, i) => (
                <div key={t.id} className="flex items-center justify-between bg-zinc-950/50 p-2 rounded-lg text-xs">
                  <span className="text-white"><span className="text-emerald-400 font-bold">#{i + 1}</span> {t.name}</span>
                  <span className="text-emerald-400 font-bold">{t.sold} vape</span>
                  <span className="text-yellow-400">${t.revenue.toFixed(0)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-red-950/30 border border-red-700/40 rounded-xl p-4">
          <p className="text-red-400 font-bold text-sm mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Bezwen Sipò (&lt;5 vape mwa sa)</p>
          {data.bottom_sellers.length === 0 ? <p className="text-zinc-500 text-xs">🎉 Pa gen okenn moun ki soulve enkyetid.</p> : (
            <div className="space-y-1">
              {data.bottom_sellers.map((t) => (
                <div key={t.id} className="flex items-center justify-between bg-zinc-950/50 p-2 rounded-lg text-xs">
                  <span className="text-white">{t.name} <span className="text-zinc-500">({(t.country || "").toUpperCase()})</span></span>
                  <span className="text-red-400 font-bold">{t.sold} vape</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// DOSSIER (NOTES) TAB
// ============================================================================
export const DossierTab = () => {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [convs, setConvs] = useState([]);
  const [nmReports, setNmReports] = useState([]);
  const [openConvId, setOpenConvId] = useState(null);
  const [section, setSection] = useState("notes"); // notes | support | nm-reports

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ceo/dossier`);
      setNotes(r.data);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  const fetchConvs = async () => {
    try {
      const r = await axios.get(`${API}/ceo/support-conversations`);
      setConvs(r.data?.conversations || []);
    } catch (e) { /* ignore */ }
  };

  const fetchNmReports = async () => {
    try {
      const r = await axios.get(`${API}/ceo/nm-monthly-reports`);
      setNmReports(r.data?.reports || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    fetchNotes();
    fetchConvs();
    fetchNmReports();
    const t = setInterval(() => { fetchConvs(); fetchNmReports(); }, 30000);
    return () => clearInterval(t);
  }, []);

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await axios.post(`${API}/ceo/dossier`, { text: newNote.trim(), source: "manual" });
      toast.success("Nòt anrejistre");
      setNewNote("");
      fetchNotes();
    } catch { toast.error("Erè"); }
  };

  const deleteNote = async (id) => {
    if (!window.confirm("Efase nòt sa?")) return;
    try {
      await axios.delete(`${API}/ceo/dossier/${id}`);
      toast.success("Efase");
      fetchNotes();
    } catch { toast.error("Erè"); }
  };

  const markConvRead = async (cid) => {
    try {
      await axios.post(`${API}/ceo/support-conversations/${cid}/mark-read`);
      fetchConvs();
    } catch {}
  };

  const markNmReportRead = async (rid) => {
    try {
      await axios.post(`${API}/ceo/nm-monthly-reports/${rid}/read`);
      fetchNmReports();
    } catch {}
  };

  const unreadSupport = convs.filter(c => c.unread_by_ceo).length;
  const unreadNm = nmReports.filter(r => !r.read_by_ceo).length;

  return (
    <div className="glass-panel p-6" data-testid="dossier-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-bold flex items-center gap-2">
          <StickyNote className="w-5 h-5 text-amber-400" /> Dosye CEO
        </h3>
        <div className="flex gap-2">
          <button onClick={() => setSection("notes")} data-testid="dossier-section-notes"
            className={`text-xs font-bold px-3 py-1.5 rounded-lg ${section === "notes" ? "bg-amber-600 text-white" : "bg-zinc-800 text-zinc-400"}`}>
            📔 Nòt ({notes.length})
          </button>
          <button onClick={() => setSection("support")} data-testid="dossier-section-support"
            className={`relative text-xs font-bold px-3 py-1.5 rounded-lg ${section === "support" ? "bg-emerald-600 text-white" : "bg-zinc-800 text-zinc-400"}`}>
            💬 Chat Sipò ({convs.length})
            {unreadSupport > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{unreadSupport}</span>}
          </button>
          <button onClick={() => setSection("nm-reports")} data-testid="dossier-section-nm-reports"
            className={`relative text-xs font-bold px-3 py-1.5 rounded-lg ${section === "nm-reports" ? "bg-purple-600 text-white" : "bg-zinc-800 text-zinc-400"}`}>
            📝 Rapò NM ({nmReports.length})
            {unreadNm > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{unreadNm}</span>}
          </button>
        </div>
      </div>

      {section === "notes" && (
        <>
          <div className="bg-zinc-900/60 rounded-xl p-3 mb-4">
            <textarea value={newNote} onChange={(e) => setNewNote(e.target.value)}
              data-testid="new-dossier-note"
              placeholder="Ekri yon rapèl / detay enpòtan..."
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm min-h-[70px]" />
            <button onClick={addNote} data-testid="add-dossier-btn"
              className="mt-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold px-4 py-2 rounded-lg flex items-center gap-1">
              <Plus className="w-4 h-4" /> Ajoute nan Dosye
            </button>
          </div>

          {loading ? <p className="text-zinc-500 text-center py-4">Chajman...</p> : notes.length === 0 ? (
            <p className="text-zinc-500 text-center py-8 text-sm">Dosye ou vid. Ajoute yon nòt pou kòmanse.</p>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto" data-testid="dossier-list">
              {notes.map(n => (
                <div key={n.id} className={`rounded-xl p-3 border ${n.source === "tanie_ai" ? "bg-violet-950/30 border-violet-700/40" : "bg-zinc-900/60 border-zinc-700"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="text-white text-sm whitespace-pre-wrap">{n.text}</p>
                      <p className="text-zinc-500 text-[10px] mt-1">
                        {n.source === "tanie_ai" ? "🤖 Tanie" : "✏️ Manuel"}
                        {n.related_user_name && ` • ${n.related_user_name}`}
                        {" • "}{new Date(n.created_at).toLocaleString("fr-FR")}
                      </p>
                    </div>
                    <button onClick={() => deleteNote(n.id)} className="text-red-400 hover:text-red-300 p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {section === "support" && (
        <div className="space-y-2 max-h-[600px] overflow-y-auto" data-testid="support-conversations-list">
          {convs.length === 0 && <p className="text-zinc-500 text-center py-8 text-sm">Poko gen chat sipò nan dosye a.</p>}
          {convs.map(c => (
            <div key={c.id} className={`rounded-xl border ${c.unread_by_ceo ? "bg-emerald-950/40 border-emerald-500/40" : "bg-zinc-900/60 border-zinc-700"}`} data-testid={`support-conv-${c.id}`}>
              <div className="flex items-center justify-between p-3 cursor-pointer" onClick={() => { setOpenConvId(openConvId === c.id ? null : c.id); if (c.unread_by_ceo) markConvRead(c.id); }}>
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center text-white font-black">{(c.agent_name || "A").charAt(0)}</div>
                  <div className="flex-1">
                    <p className="text-white font-bold text-sm">{c.user_name || "—"} <span className="text-zinc-500 font-normal">({c.user_type})</span></p>
                    <p className="text-zinc-500 text-[11px]">Ajan: <span className="text-emerald-400 font-bold">{c.agent_name}</span> • {c.user_country || "—"} • {c.messages?.length || 0} mesaj</p>
                  </div>
                  {c.unread_by_ceo && <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">NOUVO</span>}
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${c.status === "open" ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-700 text-zinc-400"}`}>{c.status}</span>
                </div>
              </div>
              {openConvId === c.id && (
                <div className="border-t border-zinc-800 p-3 space-y-2 bg-black/30" data-testid={`support-conv-messages-${c.id}`}>
                  {(c.messages || [])
                    .filter(m => !(m.content === "[greet]" || m.content === "[end]"))  // hide system placeholders
                    .map((m, i) => {
                      const isUser = m.role === "user";
                      return (
                        <div key={i} className={`flex ${isUser ? "justify-end" : "justify-start"} gap-2`}>
                          {!isUser && (
                            <div className="w-7 h-7 rounded-full bg-emerald-600 flex-shrink-0 flex items-center justify-center text-white text-[11px] font-black">
                              {(c.agent_name || "A").charAt(0)}
                            </div>
                          )}
                          <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-xs ${isUser ? "bg-violet-600 text-white rounded-br-sm" : "bg-zinc-800 text-zinc-100 rounded-bl-sm"}`}>
                            <p className="text-[9px] uppercase font-black opacity-70 mb-0.5 tracking-widest">
                              {isUser ? `👤 ${c.user_name || "stocker"}` : `🎧 ${c.agent_name || "ajan"}`}
                            </p>
                            <p className="whitespace-pre-wrap">{m.content}</p>
                            <p className="text-[9px] opacity-60 mt-1">{m.at ? new Date(m.at).toLocaleString("fr-FR") : ""}</p>
                          </div>
                          {isUser && (
                            <div className="w-7 h-7 rounded-full bg-violet-600 flex-shrink-0 flex items-center justify-center text-white text-[11px] font-black">
                              {(c.user_name || "U").charAt(0)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  {((c.messages || []).filter(m => !(m.content === "[greet]" || m.content === "[end]")).length === 0) && (
                    <p className="text-zinc-500 text-xs text-center py-4">Pa gen okenn mesaj ki echanj ankò.</p>
                  )}
                  <p className="text-zinc-500 text-[10px] text-center pt-2 border-t border-zinc-800 mt-2">
                    Total: <span className="text-white font-bold">{(c.messages || []).filter(m => !(m.content === "[greet]" || m.content === "[end]")).length}</span> mesaj
                    {" • "}Stocker: <span className="text-violet-400">{(c.messages || []).filter(m => m.role === "user" && !(m.content === "[greet]" || m.content === "[end]")).length}</span>
                    {" • "}Ajan: <span className="text-emerald-400">{(c.messages || []).filter(m => m.role !== "user").length}</span>
                    {" • "}Kòmanse: {c.started_at ? new Date(c.started_at).toLocaleString("fr-FR") : "—"}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {section === "nm-reports" && (
        <div className="space-y-2 max-h-[600px] overflow-y-auto" data-testid="nm-reports-list">
          {nmReports.length === 0 && <p className="text-zinc-500 text-center py-8 text-sm">Poko gen rapò mansyèl NM.</p>}
          {nmReports.map(r => (
            <div key={r.id} className={`rounded-xl p-3 border ${!r.read_by_ceo ? "bg-purple-950/30 border-purple-500/40" : "bg-zinc-900/60 border-zinc-700"}`} data-testid={`nm-report-${r.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div>
                  <p className="text-white font-bold text-sm">{r.title}</p>
                  <p className="text-zinc-500 text-[10px]">{r.nm_name} • {r.nm_country || "—"} • {r.month}</p>
                </div>
                {!r.read_by_ceo && (
                  <button onClick={() => markNmReportRead(r.id)} className="bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-bold px-2 py-1 rounded" data-testid={`nm-report-mark-read-${r.id}`}>
                    <CheckCircle className="w-3 h-3 inline" /> Li
                  </button>
                )}
              </div>
              <p className="text-zinc-300 text-xs whitespace-pre-wrap">{r.content}</p>
              <p className="text-zinc-500 text-[10px] mt-1">{new Date(r.created_at).toLocaleString("fr-FR")}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// PERFORMANCE OVERVIEW TAB
// ============================================================================
export const PerformanceOverviewTab = () => {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const fetchOverview = async () => {
    try {
      const r = await axios.get(`${API}/ceo/performance-overview`);
      setData(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchOverview(); }, []);

  const openDetail = async (userId) => {
    setSelected(userId);
    try {
      const r = await axios.get(`${API}/ceo/performance-score/${userId}`);
      setDetail(r.data);
    } catch { setDetail(null); }
  };

  if (!data) return <div className="text-center py-10 text-zinc-500">Chajman...</div>;

  const riskColors = {
    critical: "bg-red-950/40 border-red-500/60 text-red-300",
    warning: "bg-orange-950/40 border-orange-500/50 text-orange-300",
    moderate: "bg-amber-950/40 border-amber-500/40 text-amber-300",
    good: "bg-emerald-950/40 border-emerald-500/40 text-emerald-300",
  };
  const riskLabels = { critical: "🚨 Kritik", warning: "⚠️ Atansyon", moderate: "🟡 Modere", good: "✅ Bon" };

  return (
    <div className="glass-panel p-6" data-testid="perf-overview-tab">
      <h3 className="text-white font-bold mb-4 flex items-center gap-2">
        <Award className="w-5 h-5 text-amber-400" /> Pèfòmans Kont Chak Stocker
      </h3>

      {/* Bucket counts */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        {["critical", "warning", "moderate", "good"].map(k => (
          <div key={k} className={`rounded-xl p-3 text-center border ${riskColors[k]}`} data-testid={`perf-bucket-${k}`}>
            <p className="text-[10px] uppercase">{riskLabels[k]}</p>
            <p className="text-3xl font-black mt-1">{data.counts[k]}</p>
          </div>
        ))}
      </div>

      {/* Lists */}
      {["critical", "warning", "moderate", "good"].map(k => (
        data.buckets[k].length > 0 && (
          <div key={k} className={`rounded-xl p-3 mb-3 border ${riskColors[k]}`}>
            <p className="font-bold text-sm mb-2">{riskLabels[k]} ({data.buckets[k].length})</p>
            <div className="space-y-1">
              {data.buckets[k].map(s => (
                <button key={s.id} onClick={() => openDetail(s.id)}
                  className="w-full flex items-center justify-between bg-zinc-950/50 p-2 rounded-lg text-xs hover:bg-zinc-900/80" data-testid={`perf-row-${s.id}`}>
                  <span className="text-white text-left">{s.name} <span className="text-zinc-500">({(s.country || "").toUpperCase()})</span></span>
                  <div className="flex items-center gap-3">
                    <span className="text-zinc-400">Score: <span className="text-white font-bold">{s.score}</span></span>
                    {s.late_incidents > 0 && <span className="text-red-400 font-bold">🕐 {s.late_incidents} reta</span>}
                    {s.alert && <span className="text-red-300 text-[10px] max-w-[300px] truncate">{s.alert}</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )
      ))}

      {/* Detail modal */}
      {selected && detail && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setSelected(null)}>
          <div className="bg-zinc-900 border-2 border-zinc-700 rounded-2xl p-6 max-w-lg w-full relative">
            <button onClick={() => setSelected(null)} className="absolute top-3 right-3 bg-zinc-800 hover:bg-red-600 text-white p-2 rounded-full"><X className="w-4 h-4" /></button>
            <h4 className="text-white font-bold text-xl mb-2">{detail.name}</h4>
            <p className="text-zinc-500 text-xs mb-4">{(detail.country || "").toUpperCase()} • {riskLabels[detail.risk]}</p>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="bg-zinc-800 rounded-lg p-3 text-center">
                <p className="text-zinc-500 text-[10px] uppercase">Score</p>
                <p className="text-white font-black text-3xl">{detail.score}</p>
              </div>
              <div className="bg-zinc-800 rounded-lg p-3 text-center">
                <p className="text-zinc-500 text-[10px] uppercase">Vent Sik %</p>
                <p className="text-emerald-400 font-black text-3xl">{detail.sell_through_pct}%</p>
              </div>
              <div className="bg-zinc-800 rounded-lg p-3 text-center">
                <p className="text-zinc-500 text-[10px] uppercase">Reta Total</p>
                <p className="text-red-400 font-black text-3xl">{detail.late_incidents}</p>
              </div>
              <div className="bg-zinc-800 rounded-lg p-3 text-center">
                <p className="text-zinc-500 text-[10px] uppercase">Total Lifetime</p>
                <p className="text-yellow-400 font-black text-3xl">{detail.total_sold}</p>
              </div>
            </div>

            {detail.alert_message && (
              <div className={`rounded-lg p-3 mb-3 border ${riskColors[detail.risk]}`}>
                <p className="font-bold text-sm">{detail.alert_message}</p>
              </div>
            )}

            <div className="bg-zinc-900/60 rounded-lg p-3 space-y-1 text-xs">
              <p className="text-zinc-400"><Clock className="inline w-3 h-3" /> Peryòd entre platfòm: <span className="text-white">{detail.created_at ? new Date(detail.created_at).toLocaleDateString("fr-FR") : "—"}</span></p>
              <p className="text-zinc-400">📅 Sik: <span className="text-white">{(detail.start_date || "").substring(0, 10)} → {(detail.end_date || "").substring(0, 10)}</span></p>
              <p className="text-zinc-400">🌐 Dènye koneksyon: <span className="text-white">{detail.last_online_at ? `${new Date(detail.last_online_at).toLocaleString("fr-FR")} (${detail.days_since_online}j pase)` : "Pa gen"}</span></p>
              <p className="text-zinc-400">📦 Stock: <span className="text-white">{detail.current_stock}/{detail.initial_stock}</span></p>
              {detail.is_currently_late && <p className="text-red-400 font-bold">⚠️ Anreta {detail.days_overdue}j</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// NM SUB-STOCKERS MANAGER (CEO can see + delete sub-stockers under an NM)
// ============================================================================
export const NmSubStockersModal = ({ nmId, nmName, onClose }) => {
  const [data, setData] = useState(null);

  const fetchSubs = async () => {
    try {
      const r = await axios.get(`${API}/ceo/network-marketers/${nmId}/sub-stockers`);
      setData(r.data);
    } catch { setData(null); }
  };

  useEffect(() => { if (nmId) fetchSubs(); }, [nmId]);

  const del = async (sid, sname) => {
    if (!window.confirm(`Efase sub-stocker ${sname} anba ${nmName}?`)) return;
    try {
      await axios.delete(`${API}/ceo/network-marketers/${nmId}/sub-stockers/${sid}`);
      toast.success("Efase");
      fetchSubs();
    } catch (e) { toast.error(e.response?.data?.detail || "Erè"); }
  };

  if (!nmId) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/90 p-4 overflow-y-auto" data-testid="nm-sub-modal" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="max-w-2xl mx-auto bg-zinc-900 border-2 border-violet-500/40 rounded-2xl p-6 my-4 relative">
        <button onClick={onClose} className="absolute top-3 right-3 bg-zinc-800 hover:bg-red-600 text-white p-2 rounded-full"><X className="w-4 h-4" /></button>
        <h4 className="text-white font-bold text-xl mb-1">👥 Sub-Stockers anba {nmName}</h4>
        <p className="text-zinc-500 text-xs mb-4">CEO gen aksè pou wè + efase sub-stockers anba yon Network Marketer.</p>

        {!data ? <p className="text-zinc-500">Chajman...</p> : data.sub_stockers.length === 0 ? (
          <p className="text-zinc-500 text-center py-8">Pa gen sub-stockers anba NM sa.</p>
        ) : (
          <div className="space-y-2">
            {data.sub_stockers.map(s => {
              const sold = (s.initial_stock || 0) - (s.current_stock || 0);
              return (
                <div key={s.id} className="bg-zinc-950/60 rounded-xl p-3 flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="text-white font-bold">{s.name}</p>
                    <p className="text-zinc-500 text-xs">Stock: {s.current_stock}/{s.initial_stock} • Vann: {sold}</p>
                  </div>
                  <button onClick={() => del(s.id, s.name)} data-testid={`del-nmsub-${s.id}`}
                    className="bg-red-700 hover:bg-red-600 text-white text-xs font-bold px-3 py-2 rounded-lg flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Efase
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};


// ============================================================================
// ACTIVITY REPORT TAB — Last Login / Last Active / Last Sale per user
// ============================================================================
export const ActivityReportTab = () => {
  const [report, setReport] = useState(null);
  const [filter, setFilter] = useState("all"); // all | inactive | active
  const [searchQuery, setSearchQuery] = useState("");
  const [creditModal, setCreditModal] = useState(null);
  const [creditDelta, setCreditDelta] = useState(0);
  const [creditReason, setCreditReason] = useState("");
  const [lifetimeModal, setLifetimeModal] = useState(null);
  const [lifetimeVal, setLifetimeVal] = useState(0);
  const [historyUserId, setHistoryUserId] = useState(null);
  const [creditScores, setCreditScores] = useState({}); // userId -> {credit_score, score_color}

  const load = () => axios.get(`${API}/ceo/activity-report`).then(r => {
    setReport(r.data);
    // For each user, fetch credit score (batched to avoid burst).
    // Fetch ALL users in batches of 10 with 100ms stagger.
    const users = r.data.users || [];
    users.forEach((u, i) => {
      setTimeout(() => {
        axios.get(`${API}/user/${u.id}/credit-score`).then(cr => {
          setCreditScores(prev => ({ ...prev, [u.id]: { credit_score: cr.data.credit_score, score_color: cr.data.score_color } }));
        }).catch(() => {});
      }, Math.floor(i / 10) * 200);
    });
  }).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  if (!report) return <div className="text-center py-10 text-zinc-500" data-testid="activity-loading">Chajman rapò aktivite...</div>;

  const filtered = report.users.filter(u => {
    if (filter === "inactive" && !u.inactive) return false;
    if (filter === "active" && u.inactive) return false;
    if (searchQuery && !u.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const submitCredit = async () => {
    if (!creditModal || !creditDelta) return;
    try {
      await axios.post(`${API}/ceo/user/${creditModal.id}/credit-adjust`, {
        delta: parseInt(creditDelta, 10), reason: creditReason || "Ajisteman CEO"
      });
      toast.success(`Credit score ${creditModal.name}: ${creditDelta > 0 ? "+" : ""}${creditDelta} pwen`);
      setCreditModal(null); setCreditDelta(0); setCreditReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erè");
    }
  };

  const submitLifetime = async () => {
    if (!lifetimeModal || lifetimeVal < 0) return;
    try {
      await axios.post(`${API}/ceo/user/${lifetimeModal.id}/modify-lifetime-sold`, {
        lifetime_sold: parseInt(lifetimeVal, 10)
      });
      toast.success(`Total ventes ${lifetimeModal.name} → ${lifetimeVal}`);
      setLifetimeModal(null); setLifetimeVal(0);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erè");
    }
  };

  const inactiveCount = report.users.filter(u => u.inactive).length;

  return (
    <div className="glass-panel p-6" data-testid="activity-report-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-white font-bold flex items-center gap-2">
          <Clock className="w-5 h-5 text-blue-400" /> Rapò Aktivite Chak Moun
        </h3>
        <div className="flex gap-2 text-xs">
          <button onClick={() => setFilter("all")} className={`px-3 py-1.5 rounded-lg ${filter === "all" ? "bg-violet-600 text-white" : "bg-zinc-800 text-zinc-400"}`} data-testid="activity-filter-all">Tout ({report.count})</button>
          <button onClick={() => setFilter("inactive")} className={`px-3 py-1.5 rounded-lg ${filter === "inactive" ? "bg-red-600 text-white" : "bg-zinc-800 text-zinc-400"}`} data-testid="activity-filter-inactive">Pa Aktif ({inactiveCount})</button>
          <button onClick={() => setFilter("active")} className={`px-3 py-1.5 rounded-lg ${filter === "active" ? "bg-emerald-600 text-white" : "bg-zinc-800 text-zinc-400"}`} data-testid="activity-filter-active">Aktif ({report.count - inactiveCount})</button>
        </div>
      </div>

      {/* SEARCH BAR */}
      <div className="mb-3 relative">
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="🔍 Chèche pa non stocker/NM..."
          className="w-full bg-zinc-900/80 border border-zinc-700 focus:border-violet-500 rounded-xl px-4 py-2.5 pl-10 text-white text-sm placeholder-zinc-500 outline-none transition-colors"
          data-testid="activity-search-input"
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">🔍</span>
        {searchQuery && (
          <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white text-xs" data-testid="activity-search-clear">✕</button>
        )}
      </div>

      <div className="space-y-2 max-h-[70vh] overflow-y-auto">
        {filtered.map(u => (
          <div key={u.id} className={`rounded-xl p-3 border ${u.inactive ? "bg-red-950/20 border-red-500/40" : "bg-zinc-900/60 border-zinc-800"}`} data-testid={`activity-row-${u.id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-white font-bold text-sm">
                  <button
                    onClick={() => setHistoryUserId(u.id)}
                    className="text-white hover:text-emerald-400 underline decoration-emerald-500/40 hover:decoration-emerald-400 underline-offset-2 transition-colors"
                    data-testid={`activity-name-link-${u.id}`}
                    title="Klike pou wè istori konplè">
                    {u.name}
                  </button>
                  <span className="text-zinc-500 text-xs"> ({u.type.toUpperCase()} • {(u.country || "").toUpperCase()})</span>
                  {u.inactive && <span className="ml-2 text-red-400 text-[10px] font-bold">🔴 PA AKTIF</span>}
                  {u.is_suspended && <span className="ml-2 text-orange-400 text-[10px] font-bold">🚫 BANNI</span>}
                  {u.previous_cycle_pending && u.previous_cycle && (u.previous_cycle.initial_stock > 0 || u.previous_cycle.remaining > 0) && (
                    <span className="ml-2 text-amber-400 text-[10px] font-bold" title="Sik anvan an poko fèmen — CEO poko peze Kontwòl Efektye" data-testid={`prev-cycle-badge-${u.id}`}>
                      ⏳ Sik anvan: {u.previous_cycle.remaining || 0} rete
                    </span>
                  )}
                </p>
                <div className="text-[11px] text-zinc-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span>🔑 Last login: <span className={u.days_since_last_login && u.days_since_last_login > 3 ? "text-orange-400" : "text-zinc-300"}>{u.days_since_last_login !== null ? `${u.days_since_last_login}j` : "janm"}</span></span>
                  <span>👁 Last active: <span className={u.days_since_last_active && u.days_since_last_active > 3 ? "text-orange-400" : "text-zinc-300"}>{u.days_since_last_active !== null ? `${u.days_since_last_active}j` : "janm"}</span></span>
                  <span>💰 Last vant: <span className={u.days_since_last_sale && u.days_since_last_sale > 7 ? "text-red-400" : "text-zinc-300"}>{u.days_since_last_sale !== null ? `${u.days_since_last_sale}j` : "janm"}</span></span>
                  <span>📦 Stock: <span className="text-zinc-300">{u.current_stock}/{u.initial_stock}</span></span>
                  <span>🎯 Total: <span className="text-emerald-400 font-bold">{u.lifetime_sold || u.total_sold || 0}</span></span>
                </div>
                {/* Inline Credit Score Bar */}
                {creditScores[u.id] && (() => {
                  const cs = creditScores[u.id];
                  const colorMap = { green: "#10b981", yellow: "#facc15", orange: "#f97316", red: "#ef4444" };
                  const bg = colorMap[cs.score_color] || "#ef4444";
                  return (
                    <div className="mt-2 flex items-center gap-2" data-testid={`activity-credit-bar-${u.id}`}>
                      <span className="text-[10px] text-zinc-500 uppercase w-14">Score</span>
                      <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${cs.credit_score}%`, background: bg, boxShadow: `0 0 8px ${bg}88` }} />
                      </div>
                      <span className="text-xs font-black" style={{ color: bg }}>{cs.credit_score}/100</span>
                    </div>
                  );
                })()}
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => setHistoryUserId(u.id)} className="bg-emerald-600/80 hover:bg-emerald-500 text-white text-[10px] font-bold px-2 py-1.5 rounded-lg" data-testid={`activity-history-${u.id}`}>📜 Istori</button>
                <button onClick={() => { setCreditModal(u); setCreditDelta(0); }} className="bg-blue-600/80 hover:bg-blue-500 text-white text-[10px] font-bold px-2 py-1.5 rounded-lg" data-testid={`credit-adjust-${u.id}`}>± Score</button>
                <button onClick={() => { setLifetimeModal(u); setLifetimeVal(u.lifetime_sold || u.total_sold || 0); }} className="bg-purple-600/80 hover:bg-purple-500 text-white text-[10px] font-bold px-2 py-1.5 rounded-lg" data-testid={`modify-lifetime-${u.id}`}>✎ Total Vant</button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-center text-zinc-500 py-6">Pa gen moun nan filtè sa a</p>}
      </div>

      {/* Credit Adjust Modal */}
      {creditModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setCreditModal(null)}>
          <div className="glass-panel p-5 max-w-sm w-full" onClick={e => e.stopPropagation()} data-testid="credit-adjust-modal">
            <h4 className="text-white font-bold mb-3">Ajiste Credit Score: {creditModal.name}</h4>
            <label className="text-zinc-400 text-xs">Delta (+ oswa −)</label>
            <input type="number" value={creditDelta} onChange={e => setCreditDelta(e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white mb-2" placeholder="e.g. -10 oswa +5" data-testid="credit-delta-input" />
            <label className="text-zinc-400 text-xs">Rezon</label>
            <input type="text" value={creditReason} onChange={e => setCreditReason(e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white mb-3" placeholder="Rezon ajisteman an" data-testid="credit-reason-input" />
            <div className="flex gap-2">
              <button onClick={submitCredit} className="flex-1 bg-blue-600 hover:bg-blue-500 text-white font-bold py-2 rounded-lg" data-testid="credit-submit">Aplike</button>
              <button onClick={() => setCreditModal(null)} className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-2 rounded-lg">Anile</button>
            </div>
          </div>
        </div>
      )}

      {/* Lifetime Modal */}
      {lifetimeModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setLifetimeModal(null)}>
          <div className="glass-panel p-5 max-w-sm w-full" onClick={e => e.stopPropagation()} data-testid="lifetime-modal">
            <h4 className="text-white font-bold mb-3">Modifye Total Vant: {lifetimeModal.name}</h4>
            <label className="text-zinc-400 text-xs">Nouvo total lifetime</label>
            <input type="number" value={lifetimeVal} onChange={e => setLifetimeVal(e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white mb-3" data-testid="lifetime-input" />
            <div className="flex gap-2">
              <button onClick={submitLifetime} className="flex-1 bg-purple-600 hover:bg-purple-500 text-white font-bold py-2 rounded-lg" data-testid="lifetime-submit">Sove</button>
              <button onClick={() => setLifetimeModal(null)} className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-2 rounded-lg">Anile</button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal — CEO can view any user's istori */}
      {historyUserId && <HistoryModal userId={historyUserId} onClose={() => setHistoryUserId(null)} />}
    </div>
  );
};


// ============================================================================
// LOW STOCK ALERT BUTTON (>=70% sold)
// ============================================================================
export const LowStockAlertsButton = () => {
  const [alerts, setAlerts] = useState({ count: 0, alerts: [] });
  const [open, setOpen] = useState(false);

  const load = () => axios.get(`${API}/ceo/low-stock-alerts?threshold=70`)
    .then(r => setAlerts(r.data)).catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, []);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`relative px-3 py-2 rounded-xl font-bold text-xs flex items-center gap-1.5 transition-all ${
          alerts.count > 0
            ? "bg-red-600/90 hover:bg-red-500 text-white animate-pulse"
            : "bg-zinc-800 hover:bg-zinc-700 text-zinc-300"
        }`}
        data-testid="low-stock-alert-btn"
        title="Moun ki preske san stock"
      >
        📦 {alerts.count > 0 ? `${alerts.count} Alèt Stock` : "Stock OK"}
        {alerts.count > 0 && (
          <span className="absolute -top-1 -right-1 bg-white text-red-600 text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center">
            {alerts.count}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-zinc-900 border-2 border-red-500/40 rounded-2xl p-5 max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="low-stock-modal">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-black text-lg flex items-center gap-2">
                📦 Alèt Stock — Moun ki bezwen renouvle
              </h4>
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-zinc-400 text-xs mb-3">Moun ki gentan vann ≥70% stock yo — pran aksyon rapid pou renouvle.</p>
            {alerts.count === 0 ? (
              <p className="text-emerald-400 text-center py-6">✅ Pa gen okenn moun ki bezwen renouvèlman ijan</p>
            ) : (
              <div className="space-y-2">
                {alerts.alerts.map(a => (
                  <div key={a.id} className={`rounded-xl p-3 border ${
                    a.urgency === "critical" ? "bg-red-950/40 border-red-500/50" :
                    a.urgency === "high" ? "bg-orange-950/40 border-orange-500/50" :
                    "bg-amber-950/40 border-amber-500/40"
                  }`} data-testid={`low-stock-row-${a.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-white font-bold text-sm">{a.name} <span className="text-zinc-500 text-xs">({a.type.toUpperCase()} • {(a.country || "").toUpperCase()})</span></p>
                      <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${
                        a.urgency === "critical" ? "bg-red-500 text-white" :
                        a.urgency === "high" ? "bg-orange-500 text-white" :
                        "bg-amber-500 text-black"
                      }`}>
                        {a.urgency === "critical" ? "🚨 KRITIK" : a.urgency === "high" ? "⚠️ WO" : "ATANSYON"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${
                          a.urgency === "critical" ? "bg-red-500" :
                          a.urgency === "high" ? "bg-orange-500" : "bg-amber-500"
                        }`} style={{ width: `${a.sold_pct}%` }} />
                      </div>
                      <span className="text-xs font-bold text-white">{a.sold_pct}%</span>
                    </div>
                    <p className="text-[10px] text-zinc-400">
                      Vann: <span className="text-emerald-400 font-bold">{a.sold_this_cycle}</span> / {a.initial_stock} • Rete: <span className="text-red-400 font-bold">{a.current_stock}</span>
                      {a.renewal_pending && <span className="ml-2 text-blue-400 font-bold">🔄 Renouvèlman ap tann</span>}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};


// ============================================================================
// CEO SYSTEM MESSAGE MODAL — Send a message to any user
// ============================================================================
export const SystemMessageButton = () => {
  const [open, setOpen] = useState(false);
  const [targetName, setTargetName] = useState("");
  const [userType, setUserType] = useState("stocker");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [candidates, setCandidates] = useState([]); // full list based on userType
  const [showDropdown, setShowDropdown] = useState(false);

  // Load candidate users when modal opens or userType changes
  useEffect(() => {
    if (!open) return;
    const endpoint = userType === "nm" ? "/network-marketers" : userType === "manager" ? "/managers" : "/stockers";
    axios.get(`${API}${endpoint}`).then(r => setCandidates(r.data || [])).catch(() => setCandidates([]));
  }, [open, userType]);

  const filteredCandidates = candidates.filter(c =>
    (c.name || "").toLowerCase().includes((targetName || "").toLowerCase())
  ).slice(0, 8);

  const send = async () => {
    if (!targetName.trim() || !message.trim()) {
      toast.error("Ranpli non ak mesaj la");
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(`${API}/ceo/system-message`, {
        target_name: targetName.trim(),
        message: message.trim(),
        user_type: userType,
      });
      toast.success(`Mesaj voye bay ${r.data.recipient}`);
      setTargetName("");
      setMessage("");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erè voye mesaj la");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="px-3 py-2 rounded-xl bg-violet-600/90 hover:bg-violet-500 text-white text-xs font-bold flex items-center gap-1.5"
        data-testid="system-message-btn"
      >
        📩 Sistèm Message
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-zinc-900 border border-violet-500/40 rounded-2xl p-5 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="system-message-modal">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-bold text-lg">📩 Voye Mesaj Sistèm</h4>
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-zinc-500 text-xs mb-3">Mesaj sa a ap parèt kòm "Message du Système Vape Yard" nan notifikasyon moun nan.</p>

            <label className="text-zinc-400 text-xs">Kalite itilizatè</label>
            <select value={userType} onChange={e => setUserType(e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white mb-2 text-sm" data-testid="system-message-usertype">
              <option value="stocker">Stocker</option>
              <option value="nm">Network Marketer</option>
              <option value="manager">Manager</option>
            </select>

            <label className="text-zinc-400 text-xs">Non — chèche pa non</label>
            <div className="relative mb-2">
              <input
                type="text"
                value={targetName}
                onChange={e => { setTargetName(e.target.value); setShowDropdown(true); }}
                onFocus={() => setShowDropdown(true)}
                placeholder="🔍 Kòmanse tape non stocker/NM/manager..."
                className="w-full bg-zinc-800 border border-zinc-700 focus:border-violet-500 rounded-lg px-3 py-2 text-white text-sm outline-none"
                data-testid="system-message-target"
              />
              {showDropdown && targetName && filteredCandidates.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl overflow-hidden z-20 max-h-60 overflow-y-auto" data-testid="system-message-dropdown">
                  {filteredCandidates.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => { setTargetName(c.name); setShowDropdown(false); }}
                      className="w-full text-left px-3 py-2 hover:bg-violet-600/30 text-white text-sm border-b border-zinc-800 last:border-0"
                      data-testid={`system-message-suggestion-${c.id}`}
                    >
                      <span className="font-bold">{c.name}</span>
                      <span className="text-zinc-500 text-xs ml-2">({(c.country || "").toUpperCase()}{c.city ? ` • ${c.city}` : ""})</span>
                    </button>
                  ))}
                </div>
              )}
              {showDropdown && targetName && filteredCandidates.length === 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-zinc-900 border border-red-500/40 rounded-lg p-2 z-20">
                  <p className="text-red-400 text-xs text-center">Pa jwenn moun ki gen non sa a</p>
                </div>
              )}
            </div>

            <label className="text-zinc-400 text-xs">Mesaj</label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Ekri mesaj ou..." rows={4} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white mb-3 text-sm resize-none" data-testid="system-message-text" />

            <div className="flex gap-2">
              <button onClick={send} disabled={sending} className="flex-1 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-bold py-2 rounded-lg flex items-center justify-center gap-2" data-testid="system-message-send">
                {sending ? <><span className="w-3 h-3 border-2 border-white/50 border-t-white rounded-full animate-spin"/> Voye...</> : "📤 Voye"}
              </button>
              <button onClick={() => setOpen(false)} className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-2 rounded-lg">Anile</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};


// ============================================================================
// PENDING NM SUB-STOCKER ACTIVATIONS — CEO approves & generates login code
// ============================================================================
export const PendingActivationsButton = () => {
  const [pending, setPending] = useState([]);
  const [open, setOpen] = useState(false);
  const [activating, setActivating] = useState(null);
  const [showCode, setShowCode] = useState(null); // {sub_id, code, name}

  const load = async () => {
    try {
      const r = await axios.get(`${API}/ceo/pending-nm-activations`);
      setPending(r.data.pending || []);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const activate = async (sub) => {
    setActivating(sub.id);
    try {
      const r = await axios.post(`${API}/ceo/nm-stocker/${sub.id}/activate`);
      setShowCode({ sub_id: sub.id, code: r.data.activation_code, name: sub.name, marketer: sub.parent_marketer_name });
      toast.success(`${sub.name} aktive ✅`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erè aktivasyon");
    } finally {
      setActivating(null);
    }
  };

  const count = pending.length;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="pending-activations-btn"
        className="relative flex items-center gap-1.5 px-3 py-2 bg-amber-600/20 hover:bg-amber-600/40 border border-amber-500/40 rounded-xl text-amber-300 font-bold text-xs uppercase tracking-widest transition-colors"
        title="NM sub-stockers ki tann aktivasyon"
      >
        <CheckCircle className="w-4 h-4" />
        <span className="hidden md:inline">Aktivasyon</span>
        {count > 0 && (
          <span className="absolute -top-1.5 -right-1.5 bg-amber-500 text-black text-[10px] font-black rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
            {count}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-[95] bg-black/85 p-4 overflow-y-auto" data-testid="pending-activations-modal" onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
          <div className="max-w-3xl mx-auto bg-zinc-900 border-2 border-amber-500/40 rounded-2xl p-6 my-4 relative">
            <button onClick={() => setOpen(false)} className="absolute top-3 right-3 bg-zinc-800 hover:bg-red-600 text-white p-2 rounded-full"><X className="w-4 h-4" /></button>
            <h4 className="text-white font-bold text-xl mb-1">⏳ Aktivasyon Sub-Stockers NM</h4>
            <p className="text-zinc-500 text-xs mb-4">Network Marketers yo mete moun sa yo. Peze <span className="text-amber-400 font-bold">Aktive Kòd</span> pou jenere kòd konektasyon epi bay moun nan dashboard nòmal li.</p>

            {pending.length === 0 ? (
              <div className="text-center py-10">
                <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                <p className="text-zinc-400">Pa gen okenn aktivasyon k ap tann.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {pending.map(sub => {
                  const total = (sub.vape_models || []).reduce((a,m) => a + (parseInt(m.quantity)||0), 0);
                  return (
                    <div key={sub.id} className="bg-zinc-950/60 border border-amber-500/20 rounded-xl p-3 flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <p className="text-white font-bold">
                          {sub.name}
                          <span className="ml-2 text-[10px] uppercase bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full">activation pending</span>
                        </p>
                        <p className="text-zinc-400 text-xs mt-0.5">
                          Anba: <span className="text-cyan-300 font-bold">{sub.parent_marketer_name || "—"}</span>
                          {" • "} {sub.parent_marketer_country ? sub.parent_marketer_country.toUpperCase() : ""}
                        </p>
                        <p className="text-zinc-500 text-[11px] mt-1">
                          {total} vape total {(sub.vape_models||[]).length > 0 && `— ${sub.vape_models.map(m => `${m.name} (${m.quantity} × $${m.price})`).join(', ')}`}
                        </p>
                      </div>
                      <button
                        onClick={() => activate(sub)}
                        disabled={activating === sub.id}
                        data-testid={`activate-sub-${sub.id}`}
                        className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:brightness-110 disabled:opacity-60 text-white font-black px-4 py-2 rounded-lg text-xs uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-emerald-500/20"
                      >
                        {activating === sub.id ? "Ap kreye kòd..." : "Aktive Kòd"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Code reveal modal */}
      {showCode && (
        <div className="fixed inset-0 z-[110] bg-black/95 flex items-center justify-center p-4" data-testid="code-reveal-modal">
          <div className="bg-gradient-to-br from-emerald-950 to-cyan-950 border-2 border-emerald-500 rounded-2xl p-6 max-w-md w-full text-center">
            <CheckCircle className="w-14 h-14 text-emerald-400 mx-auto mb-3" />
            <h3 className="text-white font-black text-xl mb-1">Kòd Konektasyon Kreye ✅</h3>
            <p className="text-emerald-300 text-sm mb-4">Pou <span className="font-bold">{showCode.name}</span> anba <span className="font-bold">{showCode.marketer}</span></p>

            <div className="bg-black/60 border-2 border-dashed border-emerald-400 rounded-xl py-6 my-3">
              <p className="text-zinc-500 text-[10px] uppercase tracking-widest">Kòd Estòker</p>
              <p className="text-white font-black text-5xl tracking-widest my-2" data-testid="revealed-activation-code">{showCode.code}</p>
              <button
                onClick={() => { navigator.clipboard.writeText(showCode.code); toast.success("Kòd kopye"); }}
                className="text-xs text-cyan-400 hover:text-cyan-300 underline"
                data-testid="copy-activation-code"
              >Kopye kòd la</button>
            </div>

            <p className="text-zinc-400 text-xs mb-4">Bay moun sa a kòd sa a. Yo ka konekte kounye a nan dashboard nòmal stocker la ak non yo ak kòd sa a.</p>

            <button
              onClick={() => setShowCode(null)}
              data-testid="close-code-reveal"
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-black py-3 rounded-xl uppercase text-sm"
            >Fèmen</button>
          </div>
        </div>
      )}
    </>
  );
};
